<ng-template
  let-extraClass="extraClass"
  #topNav>
  @if (!isRightSideContent) {
    <tidy-top-nav
      [class]="extraClass"
      [title]="pageTitle"
      [canGoBack]="true"
      [noBow]="true" />
  }
</ng-template>

<ng-container *ngTemplateOutlet="topNav;context:{ extraClass: 'mobile' }" />

<ng-template #flowArrow>
  <tidy-row [align]="'center'" class="rule-flow-arrow">
    <tidy-image
      [src]="'assets/svg/arrow-down-outline.svg'"
      class="header-size" />
  </tidy-row>
</ng-template>

<ng-template #content>
  <form [formGroup]="form">
    <tidy-wrapper [class]="isRightSideContent ? 'finance-skeleton right-side-scroll-enabled' : 'finance-skeleton'">
      @if (!loaded) {
        <ion-skeleton-text [animated]="true" style="width: 45%; height: 20px; border-radius: 4px; margin-bottom: 12px;" />
        @for (i of [1,2,3]; track i) {
          <ion-skeleton-text [animated]="true" style="width: 100%; height: 44px; border-radius: 8px; margin-bottom: 12px;" />
        }
        <ion-skeleton-text [animated]="true" style="width: 100%; height: 72px; border-radius: 12px; margin: 20px 0;" />
        <ion-skeleton-text [animated]="true" style="width: 30%; height: 20px; border-radius: 4px; margin-bottom: 12px;" />
        @for (i of [1,2,3]; track i) {
          <ion-skeleton-text [animated]="true" style="width: 60%; height: 18px; border-radius: 4px; margin-bottom: 10px;" />
        }
      }

      @if (loaded) {
        <tidy-row>
          <tidy-text [header]="'When a bank transaction…'" />
        </tidy-row>
        <div formArrayName="textConditions">
          @for (group of textConditions.controls; track group; let i = $index) {
            <div [formGroupName]="i">
              <tidy-row>
                <tidy-select
                  [label]="'Description'"
                  formControlName="op"
                  [form]="group.controls.op"
                  [items]="operatorItems"
                  [hideSearch]="true"
                  [icon]="'assets/svg/text-outline.svg'" />
              </tidy-row>
              <tidy-row>
                <div class="rule-text-row">
                  <div class="rule-text-row__input">
                    <tidy-input
                      [label]="group.controls.op.value === 'matches' ? 'Pattern' : 'Text'"
                      formControlName="value"
                      [form]="group.controls.value"
                      [icon]="'assets/svg/create-outline.svg'"
                      [submitted]="submitted"
                      [errorMessage]="'Enter the text to match'" />
                  </div>
                  <tidy-header-select
                    class="rule-text-row__picker"
                    [items]="sampleItems"
                    [placeholder]="'Search recent transactions'"
                    [icon]="'assets/svg/search-outline.svg'"
                    (optionChange)="onSampleSelected($event, i)" />
                </div>
              </tidy-row>
              @if (textConditions.length > 1) {
                <tidy-row>
                  <tidy-text
                    [actionBody]="'Remove this condition'"
                    (action)="removeTextCondition(i)"
                    class="link red" />
                </tidy-row>
              }
            </div>
          }
        </div>
        <tidy-row>
          <tidy-text
            [actionBody]="'+ Add another description condition'"
            (action)="addTextCondition()"
            class="link" />
        </tidy-row>
        <tidy-row>
          <tidy-select
            [label]="'Transaction type'"
            formControlName="transactionType"
            [form]="form.controls.transactionType"
            [items]="transactionTypeItems"
            [hideSearch]="true"
            [icon]="'assets/svg/swap-horizontal-outline.svg'" />
        </tidy-row>

        <ng-container *ngTemplateOutlet="flowArrow" />

        <tidy-alert class="black-background">
          @if (regexError) {
            <tidy-row>
              <tidy-text [body]="regexError" [translate]="false" class="red" />
            </tidy-row>
          } @else if (!previewReady) {
            <tidy-row>
              <tidy-text [body]="'Checking your past bank transactions…'" />
            </tidy-row>
          } @else if (matchCount === 0) {
            <tidy-row>
              <tidy-text [body]="'No past bank transactions match yet.'" />
            </tidy-row>
          } @else {
            <tidy-row>
              <tidy-text
                [header]="'Matches ' + matchCount + ' past ' + (matchCount === 1 ? 'transaction' : 'transactions')"
                [translate]="false" />
            </tidy-row>
            @for (example of matchExamples; track example) {
              <tidy-row>
                <tidy-text [body]="example" [translate]="false" />
              </tidy-row>
            }
            @if (matchCount > matchExamples.length) {
              <tidy-row>
                <tidy-text [helper]="'and ' + (matchCount - matchExamples.length) + ' more'" [translate]="false" />
              </tidy-row>
            }
          }
        </tidy-alert>

        <ng-container *ngTemplateOutlet="flowArrow" />

        <tidy-row>
          <tidy-text [header]="'Then…'" />
        </tidy-row>
        <tidy-row>
          <tidy-radio-button
            formControlName="classification"
            [form]="form.controls.classification"
            [items]="classificationItems"
            [submitted]="submitted"
            [errorMessage]="'Choose what should happen'" />
        </tidy-row>
        @if (isPnl) {
          <tidy-row>
            <tidy-select
              [label]="'Category'"
              formControlName="monthly_finance_type_key"
              [form]="form.controls.monthly_finance_type_key"
              [items]="categoryItems"
              [icon]="'assets/svg/list-outline.svg'"
              [submitted]="submitted"
              [errorMessage]="'Please select a category'" />
          </tidy-row>
          <tidy-row>
            <tidy-select
              [label]="'Property'"
              formControlName="address_id"
              [form]="form.controls.address_id"
              [items]="propertyItems"
              [icon]="'assets/svg/home-outline.svg'" />
          </tidy-row>
        }

        @if (errorMessage) {
          <tidy-error-message [text]="errorMessage" />
        }

        <tidy-button
          [text]="isEdit ? 'Save Rule' : 'Create Rule'"
          [action]="boundSave"
          [disabled]="saving"
          class="primary" />

        <tidy-row [align]="'center'">
          <tidy-text [helper]="'Applies to new bank transactions only. Nothing already categorized changes.'" />
        </tidy-row>
      }
    </tidy-wrapper>
  </form>
</ng-template>

@if (!isRightSideContent) {
  <app-scroll-wrapper [templateContent]="content" />
}

@if (isRightSideContent) {
  <ion-content class="ion-content-right-column">
    <ng-container *ngTemplateOutlet="content" />
  </ion-content>
}
